import React, { useEffect, useRef, useState } from "react";
import { Control, useController, UseFormTrigger } from "react-hook-form";
import Select, { StylesConfig } from "react-select";
import { useTranslation } from "react-i18next";

import css from "./styles.module.scss";

// The form value is always stored as the backend-compatible string `"<n> mins"`
// (total minutes). Presets mirror the historical dropdown; a "Custom…" entry
// reveals separate Hours + Minutes inputs, capped at 24 hours total.
export const MAX_DURATION_MINUTES = 24 * 60;

// The most-used lengths (≈91% of existing rates); anything else uses "Custom…".
export const DURATION_PRESETS: { value: string; label: string }[] = [
  { value: "30 mins", label: "30 minutes" },
  { value: "45 mins", label: "45 minutes" },
  { value: "60 mins", label: "60 minutes" },
  { value: "120 mins", label: "2 hours" },
];

const CUSTOM_VALUE = "__custom__";

// "50 mins" -> 50, "2 hours" -> 120. Returns "" when empty/unparseable.
const toTotalMinutes = (value?: string): number | "" => {
  const match = String(value ?? "").match(/^(\d+)\s*(hours?|mins?)$/i);
  if (!match) {
    return "";
  }
  const amount = parseInt(match[1], 10);
  if (isNaN(amount)) {
    return "";
  }
  return /hour/i.test(match[2]) ? amount * 60 : amount;
};

// Match a stored value to a preset by TOTAL MINUTES, so backend formats like
// "2 hours" resolve to the "120 mins" preset instead of falling through to
// "Custom…". Returns undefined when the value matches no preset.
const presetForValue = (value?: string) => {
  const total = toTotalMinutes(value);
  if (total === "") {
    return undefined;
  }
  return DURATION_PRESETS.find((preset) => toTotalMinutes(preset.value) === total);
};

// Split a stored value into { hours, minutes } strings for the two inputs.
const partsFromValue = (value?: string): { hours: string; minutes: string } => {
  const total = toTotalMinutes(value);
  if (total === "") {
    return { hours: "", minutes: "" };
  }
  return {
    hours: String(Math.floor(total / 60)),
    minutes: String(total % 60),
  };
};

const clampDigits = (raw: string, max: number): string => {
  const digits = raw.replace(/\D/g, "");
  if (digits === "") {
    return "";
  }
  const parsed = Math.min(parseInt(digits, 10), max);
  return String(parsed);
};

// "1 hours" reads as a typo. The boxes hold a count, so their unit follows it:
// one is singular, everything else -- including the empty box, which stands for
// zero -- is plural.
const isOne = (value: string): boolean => parseInt(value, 10) === 1;

const IndicatorSeparator = (): JSX.Element | null => null;

interface DurationSelectProps {
  control: Control<any>;
  name?: string;
  trigger?: UseFormTrigger<any>;
  styles?: StylesConfig;
  className?: string;
  /**
   * react-select class prefix. Keep the default: the E2E suite drives this
   * dropdown through `.duration-select__control` / `.duration-select__menu`.
   */
  classNamePrefix?: string;
  onChanged?: () => void;
}

export default function DurationSelect({
  control,
  name = "duration",
  trigger,
  styles,
  className,
  classNamePrefix = "duration-select",
  onChanged,
}: DurationSelectProps) {
  const { t } = useTranslation();
  const { field } = useController({ control, name });

  const isCustomValue = (value?: string): boolean =>
    !!value && !presetForValue(value);

  const initialParts = partsFromValue(field.value);
  const initialCustom = isCustomValue(field.value);
  const [customActive, setCustomActive] = useState<boolean>(initialCustom);
  const [hoursStr, setHoursStr] = useState<string>(
    initialCustom ? initialParts.hours : ""
  );
  const [minutesStr, setMinutesStr] = useState<string>(
    initialCustom ? initialParts.minutes : ""
  );
  // Refs mirror the inputs so each change commits with the other field's live
  // value without waiting for a re-render.
  const hoursRef = useRef<string>(hoursStr);
  const minutesRef = useRef<string>(minutesStr);
  // The last value THIS component wrote. Lets the sync effect ignore our own
  // writes (incl. a custom value that happens to equal a preset) and react only
  // to EXTERNAL changes — parent reset/seed/setValue.
  const ownValueRef = useRef(field.value);

  // Sync custom mode to external value changes only (an edit form seeding via
  // reset, or the parent clearing/resetting the field). Our own writes are
  // skipped so typing in custom mode never bounces the dropdown.
  useEffect(() => {
    if (field.value === ownValueRef.current) {
      return;
    }
    ownValueRef.current = field.value;

    if (isCustomValue(field.value)) {
      const parts = partsFromValue(field.value);
      setHoursStr(parts.hours);
      setMinutesStr(parts.minutes);
      hoursRef.current = parts.hours;
      minutesRef.current = parts.minutes;
      setCustomActive(true);
    } else {
      // External empty or preset value → leave custom mode and clear the inputs.
      setCustomActive(false);
      setHoursStr("");
      setMinutesStr("");
      hoursRef.current = "";
      minutesRef.current = "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.value]);

  const options = [
    ...DURATION_PRESETS.map((preset) => ({
      value: preset.value,
      label: t(preset.label),
    })),
    { value: CUSTOM_VALUE, label: `${t("Custom")}…` },
  ];

  const selectValue = customActive
    ? options[options.length - 1]
    : (() => {
        const preset = presetForValue(field.value);
        return preset
          ? options.find((option) => option.value === preset.value) ?? null
          : null;
      })();

  const commitParts = (hours: string, minutes: string) => {
    const hoursNum = hours === "" ? 0 : parseInt(hours, 10);
    const minutesNum = minutes === "" ? 0 : parseInt(minutes, 10);
    let total = (isNaN(hoursNum) ? 0 : hoursNum) * 60 + (isNaN(minutesNum) ? 0 : minutesNum);

    if (total > MAX_DURATION_MINUTES) {
      total = MAX_DURATION_MINUTES;
      hours = String(Math.floor(total / 60));
      minutes = String(total % 60);
      setHoursStr(hours);
      setMinutesStr(minutes);
    }

    hoursRef.current = hours;
    minutesRef.current = minutes;
    const nextValue = hours === "" && minutes === "" ? "" : total < 1 ? "" : `${total} mins`;
    ownValueRef.current = nextValue;
    field.onChange(nextValue);
    trigger?.(name);
    onChanged?.();
  };

  const handleSelect = (selectedOption: unknown) => {
    const value = (selectedOption as { value: string })?.value || "";
    if (value === CUSTOM_VALUE) {
      setCustomActive(true);
      // Start fresh — clear any stale inputs so the UI matches the now-empty
      // value (wait for Hours/Minutes; don't flag "required" yet).
      setHoursStr("");
      setMinutesStr("");
      hoursRef.current = "";
      minutesRef.current = "";
      ownValueRef.current = "";
      field.onChange("");
    } else {
      setCustomActive(false);
      ownValueRef.current = value;
      field.onChange(value);
      trigger?.(name);
    }
    onChanged?.();
  };

  const handleHoursChange = (raw: string) => {
    const hours = clampDigits(raw, 24);
    setHoursStr(hours);
    commitParts(hours, minutesRef.current);
  };

  // Not capped at 59 while typing: a coach thinking in minutes types "90" for
  // an hour and a half, and a field that refuses the second digit reads as
  // broken. The total is right either way; the two boxes are tidied up on the
  // way out (below).
  const handleMinutesChange = (raw: string) => {
    const minutes = clampDigits(raw, MAX_DURATION_MINUTES);
    setMinutesStr(minutes);
    commitParts(hoursRef.current, minutes);
  };

  // "90 minutes" becomes "1 hour 30 minutes" once the field is left, rather
  // than mid-keystroke -- which would rewrite "9" into "0 hours 9 minutes"
  // before the coach had finished typing.
  const normalizeParts = () => {
    const hoursNum = hoursRef.current === "" ? 0 : parseInt(hoursRef.current, 10);
    const minutesNum = minutesRef.current === "" ? 0 : parseInt(minutesRef.current, 10);

    if (isNaN(minutesNum) || minutesNum < 60) {
      return;
    }

    const total = Math.min(
      (isNaN(hoursNum) ? 0 : hoursNum) * 60 + minutesNum,
      MAX_DURATION_MINUTES,
    );
    const hours = String(Math.floor(total / 60));
    const minutes = String(total % 60);

    setHoursStr(hours);
    setMinutesStr(minutes);
    commitParts(hours, minutes);
  };

  const hoursLabel = isOne(hoursStr) ? t("hour") : t("hours");
  const minutesLabel = isOne(minutesStr) ? t("minute") : t("minutes");

  const blockNonDigitKeys = (e: React.KeyboardEvent) => {
    if (["-", "e", "+", ".", ","].includes(e.key)) {
      e.preventDefault();
    }
  };

  return (
    <div className={css.container}>
      <Select
        id={name}
        value={selectValue}
        onChange={handleSelect}
        options={options}
        styles={{
          ...(styles as StylesConfig),
          menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
        }}
        className={className}
        classNamePrefix={classNamePrefix}
        components={{ IndicatorSeparator }}
        placeholder={t("Select Duration")}
        isSearchable={false}
        // The menu floats over the page instead of growing the modal it sits
        // in: an inline menu near the bottom of a scrollable modal pushes the
        // modal past its own box and the scrollbar turns up outside it
        // (CFTB-3534). Portalled and fixed, so it is positioned against the
        // window and no container's scroll height changes; `auto` flips it
        // upwards when there is no room below.
        menuPortalTarget={
          typeof document !== "undefined" ? document.body : undefined
        }
        menuPosition="fixed"
        menuPlacement="auto"
      />
      {customActive && (
        <div className={css.customRow}>
          <div className={css.inputBox}>
            <input
              type="number"
              min={0}
              max={24}
              value={hoursStr}
              placeholder="0"
              aria-label={hoursLabel}
              className={css.numInput}
              onKeyDown={blockNonDigitKeys}
              onChange={(e) => handleHoursChange(e.target.value)}
            />
            <span className={css.suffix}>{hoursLabel}</span>
          </div>
          <div className={css.inputBox}>
            <input
              type="number"
              min={0}
              max={MAX_DURATION_MINUTES}
              value={minutesStr}
              placeholder="00"
              aria-label={minutesLabel}
              className={css.numInput}
              onKeyDown={blockNonDigitKeys}
              onBlur={normalizeParts}
              onChange={(e) => handleMinutesChange(e.target.value)}
            />
            <span className={css.suffix}>{minutesLabel}</span>
          </div>
        </div>
      )}
    </div>
  );
}
